import dataLayerApi from "./dataLayerApi";
import {
  dataAttributes,
  getDataFromTargetOrThrow,
  getHrefFromTarget,
  tryGetDataFromTarget,
} from "./get-data-from-entry";
import { querySelectorAllDeep } from "./query-selector";
import { isHtmlElement } from "./closest-data-sit-attribute";

export const THRESHOLD = 300;

export function parseBoolean(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v === "true" || v === "1";
}

export function parseStringArray(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed) && parsed.every((x) => typeof x === "string")) {
      return parsed;
    }
  } catch {
    // fall through to CSV
  }
  return trimmed
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

const teaserObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (isHtmlElement(entry.target)) {
        const element = entry.target as HTMLElement;

        if (!entry.isIntersecting) {
          const timeoutId = element.dataset[dataAttributes.teaserTimeout];
          if (timeoutId) {
            clearTimeout(Number(timeoutId));
          }
          return;
        }

        element.dataset[dataAttributes.teaserTimeout] = String(
          setTimeout(() => {
            const destination = getHrefFromTarget(element) ?? "";

            const label =
              tryGetDataFromTarget(element, dataAttributes.teaserLabel) ?? "";

            const headline = getDataFromTargetOrThrow(
              element,
              dataAttributes.teaserHeadline,
            )!;

            const location =
              tryGetDataFromTarget(element, dataAttributes.location) ?? "";

            const variant =
              tryGetDataFromTarget(element, dataAttributes.variant) ?? "";

            const detail =
              tryGetDataFromTarget(element, dataAttributes.detail) ?? "";

            const topic =
              tryGetDataFromTarget(element, dataAttributes.topic) ?? "";
            const mustRead = parseBoolean(
              tryGetDataFromTarget(element, dataAttributes.mustRead) ?? "",
            );
            const isAccessibleExternally = parseBoolean(
              tryGetDataFromTarget(
                element,
                dataAttributes.isAccessibleExternally,
              ) ?? "",
            );
            const targetGroup = parseStringArray(
              tryGetDataFromTarget(element, dataAttributes.targetGroup) ?? "",
            );
            const theme = parseStringArray(
              tryGetDataFromTarget(element, dataAttributes.theme) ?? "",
            );
            const division = parseStringArray(
              tryGetDataFromTarget(element, dataAttributes.division) ?? "",
            );
            const postType = parseStringArray(
              tryGetDataFromTarget(element, dataAttributes.postType) ?? "",
            );
            const contentRegion = parseStringArray(
              tryGetDataFromTarget(element, dataAttributes.contentRegion) ?? "",
            );

            dataLayerApi.trackTeaserImpression({
              destination,
              label,
              headline,
              component: element,
              location,
              variant,
              detail,
              topic,
              mustRead,
              isAccessibleExternally,
              targetGroup,
              theme,
              division,
              postType,
              contentRegion,
            });

            element.dataset[dataAttributes.teaserImpressed] = "";
            unobserveTeaserElement(element);
          }, THRESHOLD),
        );
      }
    });
  },
  { rootMargin: "0px 0px -30% 0px" },
);

export function startObservingTeaserElements() {
  Array.from(
    querySelectorAllDeep('[data-sit-teaser]:not([data-sit-teaser="manual"])'),
  ).forEach((element) => observeTeaserElement(element));
}

export function unobserveTeaserElement(element: HTMLElement) {
  teaserObserver.unobserve(element);
}

export function observeTeaserElement(element: HTMLElement) {
  if (element.dataset[dataAttributes.teaserImpressed] === undefined) {
    teaserObserver.observe(element);
  }
}

const impressionObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (isHtmlElement(entry.target)) {
        const element = entry.target;
        if (!entry.isIntersecting) {
          const timeoutId = element.dataset[dataAttributes.impressionTimeout];
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          return;
        }

        element.dataset[dataAttributes.impressionTimeout] = String(
          setTimeout(() => {
            dataLayerApi.trackImpression({
              type: getDataFromTargetOrThrow(
                element,
                dataAttributes.impressionType,
              )!,
              label:
                tryGetDataFromTarget(element, dataAttributes.impressionLabel) ??
                "",
              headline:
                tryGetDataFromTarget(
                  element,
                  dataAttributes.impressionHeadline,
                ) ?? "",
              component: element,
              location:
                tryGetDataFromTarget(element, dataAttributes.location) ?? "",
              variant:
                tryGetDataFromTarget(element, dataAttributes.variant) ?? "",
              detail:
                tryGetDataFromTarget(element, dataAttributes.detail) ?? "",
            });
            element.dataset[dataAttributes.teaserImpressed] = "";
            unobserveImpressionElement(element);
          }, THRESHOLD),
        );
      }
    });
  },
  { rootMargin: "0px 0px 0px 0px" },
);

export function startObservingImpressionElements() {
  Array.from(querySelectorAllDeep("[data-sit-impression]")).forEach((element) =>
    observeImpressionElement(element),
  );
}

export function unobserveImpressionElement(element: HTMLElement) {
  impressionObserver.unobserve(element);
}

export function observeImpressionElement(element: HTMLElement) {
  if (element.dataset[dataAttributes.teaserImpressed] === undefined) {
    impressionObserver.observe(element);
  }
}
